import { motion } from "framer-motion";
import {
  Target,
  Signal,
  MessageSquareText,
  ListOrdered,
  BookOpen,
  Compass,
  Gauge,
  Heart,
} from "lucide-react";
import { Container, Chapter, FadeUp } from "./primitives";

const CARDS = [
  { icon: Target, k: "Intent Detection", d: "Classify every thread by buying, curiosity or noise." },
  { icon: Signal, k: "Buying Signal Recognition", d: "Recognize purchase phrases across languages and platforms." },
  { icon: MessageSquareText, k: "Comment Generation", d: "Draft in your brand voice grounded in the context." },
  { icon: ListOrdered, k: "Lead Prioritization", d: "Rank prospects by score, freshness and fit." },
  { icon: BookOpen, k: "Conversation Summaries", d: "Long threads distilled to the parts that matter." },
  { icon: Compass, k: "Recommended Next Action", d: "Reply, DM, wait, or disqualify — with reasoning." },
  { icon: Gauge, k: "AI Confidence Score", d: "Transparent probabilities on every recommendation." },
  { icon: Heart, k: "Sentiment Analysis", d: "Understand tone before you type a single word." },
];

export default function AISection() {
  return (
    <section id="ai" data-testid="ai-section" className="relative py-32 md:py-40 border-t border-white/10">
      <div className="orb bg-[#22D3EE] w-[420px] h-[420px] top-1/2 -left-40 opacity-25" />
      <Container className="relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <Chapter
            number="04"
            kicker="The Intelligence"
            title={
              <>
                Your AI sales assistant{" "}
                <span className="font-serif-x italic">never</span> sleeps.
              </>
            }
            description="A layered intelligence system trained on outbound context. It reads, ranks, recommends and refines — while your team focuses on closing."
          />
          <FadeUp delay={0.2}>
            <div className="glass rounded-2xl px-5 py-4 flex items-center gap-4 min-w-[240px]">
              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#4F46E5]">
                <span className="absolute inset-0 rounded-xl bg-white/10 animate-ping opacity-30" />
                <Signal className="h-4 w-4" />
              </div>
              <div>
                <div className="font-mono-x text-[10px] uppercase tracking-[0.22em] text-white/40">Model</div>
                <div className="text-sm">SocialLead L3 · v3.2</div>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8 rounded-3xl overflow-hidden border border-white/10">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.k}
              whileHover={{ backgroundColor: "rgba(255,255,255,0.03)" }}
              className="relative group p-8 bg-[var(--bg)] min-h-[260px] flex flex-col"
            >
              {/* Tracing beam on hover */}
              <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent" />
              </span>

              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] group-hover:border-white/30 transition-colors">
                  <c.icon className="h-4 w-4 text-white/80" strokeWidth={1.5} />
                </span>
                <span className="font-mono-x text-[10px] tracking-[0.22em] uppercase text-white/25">
                  AI/{String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <h4 className="mt-auto font-display text-xl md:text-2xl tracking-[-0.02em] leading-tight text-white">
                {c.k}
              </h4>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
